import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  SelectChangeEvent,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Download,
  Search,
  FilterList,
  Verified,
  Shield,
  Gavel,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface AuditLog {
  id: string;
  event_type: string;
  event_category: string;
  severity: string;
  phi_categories: string[];
  action_taken: string;
  request_ip: string;
  created_at: string;
  verified: boolean;
  rule_name?: string;
  rule_id?: string;
  framework?: string;
}

interface AuditLogViewerProps {
  organizationId?: number;
}

const AuditLogViewer: React.FC<AuditLogViewerProps> = ({ organizationId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [eventCategory, setEventCategory] = useState<string>('');
  const [severity, setSeverity] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const { data: auditData, isLoading, error, refetch } = useQuery({
    queryKey: ['auditLogs', page, rowsPerPage, searchQuery, eventCategory, severity, startDate, endDate],
    queryFn: async () => {
      const params: any = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };

      if (searchQuery) {
        const response = await axios.get('/api/audit/search', {
          params: { ...params, q: searchQuery, event_category: eventCategory, severity }
        });
        return response.data;
      }

      if (eventCategory) params.event_category = eventCategory;
      if (severity) params.severity = severity;
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();

      const response = await axios.get('/api/audit/logs', { params });
      return response.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setEventCategory(event.target.value);
    setPage(0);
  };

  const handleSeverityChange = (event: SelectChangeEvent<string>) => {
    setSeverity(event.target.value);
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const handleExport = async () => {
    try {
      const params: any = {};
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();
      params.format = 'csv';

      const response = await axios.get('/api/audit/export', {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'phi_detection': return 'primary';
      case 'breach_attempt': return 'error';
      case 'access_control': return 'warning';
      case 'configuration_change': return 'info';
      default: return 'default';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'block': return 'error';
      case 'redact': return 'warning';
      case 'alert': return 'info';
      case 'allow': return 'success';
      default: return 'default';
    }
  };

  const getFrameworkIcon = (framework?: string) => {
    if (!framework) return null;
    switch (framework) {
      case 'HIPAA': return <Shield fontSize="small" />;
      case 'GDPR': return <Gavel fontSize="small" />;
      case 'SOC2': return <SecurityIcon fontSize="small" />;
      case 'ISO27001': return <Shield fontSize="small" />;
      case 'NIST80053': return <SecurityIcon fontSize="small" />;
      default: return <SecurityIcon fontSize="small" />;
    }
  };

  const getFrameworkColor = (framework?: string): 'error' | 'warning' | 'info' | 'success' | 'default' => {
    if (!framework) return 'default';
    switch (framework) {
      case 'HIPAA': return 'error';
      case 'GDPR': return 'warning';
      case 'SOC2': return 'info';
      case 'ISO27001': return 'success';
      case 'NIST80053': return 'info';
      default: return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load audit logs. Please try again.
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">Audit Logs</Typography>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
            size="small"
          >
            Export
          </Button>
        </Box>

        {/* Filters */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={handleSearch}>
                    <Search />
                  </IconButton>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={eventCategory}
                onChange={handleCategoryChange}
                label="Category"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="phi_detection">PHI Detection</MenuItem>
                <MenuItem value="breach_attempt">Breach Attempt</MenuItem>
                <MenuItem value="access_control">Access Control</MenuItem>
                <MenuItem value="configuration_change">Configuration</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select
                value={severity}
                onChange={handleSeverityChange}
                label="Severity"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>

        {/* Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Event Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Rule</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>PHI Categories</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell align="center">Verified</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditData?.logs.map((log: AuditLog) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    {new Date(log.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {log.event_type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.event_category}
                      size="small"
                      color={getCategoryColor(log.event_category)}
                    />
                  </TableCell>
                  <TableCell>
                    {log.rule_name ? (
                      <Box>
                        <Chip
                          icon={getFrameworkIcon(log.framework) || undefined}
                          label={log.rule_name}
                          size="small"
                          color={getFrameworkColor(log.framework)}
                          variant="outlined"
                        />
                        {log.framework && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {log.framework}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.severity}
                      size="small"
                      color={getSeverityColor(log.severity)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.action_taken}
                      size="small"
                      color={getActionColor(log.action_taken)}
                    />
                  </TableCell>
                  <TableCell>
                    {log.phi_categories && log.phi_categories.length > 0 ? (
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {log.phi_categories.slice(0, 2).map((cat, idx) => (
                          <Chip key={idx} label={cat} size="small" variant="outlined" />
                        ))}
                        {log.phi_categories.length > 2 && (
                          <Chip label={`+${log.phi_categories.length - 2}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {log.request_ip || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {log.verified ? (
                      <Tooltip title="Signature verified">
                        <Verified color="success" fontSize="small" />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={auditData?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </CardContent>
    </Card>
  );
};

export default AuditLogViewer;
